import { debug } from '../logger';
import { AliOSSUploader } from '../net/uploader/alioss';
import type { UploadOSSConfig } from 'zf-infra';
import type { CompressParams } from './types';

class Media {

  /**
   * 获取图片的宽高
   * @param src
   * @returns
   */
  async getImageSize(src: string): Promise<{ width: number; height: number }> {
    const img = new Image();
    return await new Promise(resolve => {
      img.src = src;
      img.onload = (): void => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = (): void => {
        console.error('image loading failed:', src);
      };
    });
  }

  getOssURL(src: string, baseName?: string, internal?: boolean): string {
    if (src.startsWith('blob:')) {
      return src;
    }
    if (src.startsWith('http://') || src.startsWith('https://')) {
      return src;
    }

    const internalParams = internal === true ? '&internal=1' : '';

    if (baseName != null) {
      return `${baseName}/api/oss/redirect?name=${src}${internalParams}`;
    }

    return `/api/oss/redirect?name=${src}${internalParams}`;
  }

  getFileType(blob: Blob): 'image' | 'video' | 'file' {
    const { type } = blob;

    if (type.startsWith('image/')) {
      return 'image';
    }

    if (type.startsWith('video/')) {
      return 'video';
    }

    return 'file';
  }

  toImage(blob: Blob): string {
    return URL.createObjectURL(blob);
  }

  /**
   * 处理图片，并覆盖原图（仅限OSS使用）
   * https://help.aliyun.com/document_detail/144582.htm
   * @param id
   * @param params
   */
  async compress(
    id: string,
    params: CompressParams,
    ossConfig?: UploadOSSConfig
  ): Promise<void> {
    debug.infra('compress', id, params);

    const client = await new AliOSSUploader().getClient(ossConfig);
    const option = this.formatCompressParams(params);
    debug.infra('compress option', option);

    // TODO: 补充不足的ts类型
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    await client.processObjectSave(id, id, option);
  }

  /**
   * 从本地上传的视频中读取第一帧，返回缩略图
   * @param {File} file - input[type="file"] 上传的 video 文件
   * @returns {Promise<File>} 缩略图
   */
  async getVideoThumbnail(file: File): Promise<Blob> {
    return await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');

      video.src = url;
      video.crossOrigin = 'anonymous';
      video.muted = true; // 部分浏览器必须静音才能播放
      video.preload = 'auto';

      // 等待视频元信息加载完成
      video.addEventListener('loadedmetadata', () => {
        // 跳到第一帧（0 秒）
        video.currentTime = 0;
      });

      // 等待 seeked 后说明画面已准备好
      video.addEventListener('seeked', () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        ctx!.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(blob => {
          URL.revokeObjectURL(url);
          if (blob == null) {
            reject(new Error('get video thumbnail failed'));
            return;
          }

          const imgFile = new File(
            [blob],
            `${file.name.replace(/\.\w+$/, '')}_poster.png`,
            { type: 'image/png' }
          );

          resolve(imgFile);
        }, 'image/png');
      });

      video.addEventListener('error', err => {
        URL.revokeObjectURL(url);
        reject(err);
      });
    });
  }

  /**
   * 转换为处理图片的参数，如 'image/resize,l_100/quality,Q_80'
   * @param params
   * @returns
   */
  private formatCompressParams(params: CompressParams): string {
    let result = 'image/format,jpg/';

    if (params.resize != null) {
      result = `${result}/resize,l_${params.resize}`;
    }

    if (params.quality != null && params.quality < 100) {
      result = `${result}/quality,Q_${params.quality.toFixed()}`;
    }

    return result;
  }

}

export const media = new Media();
